import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useToast } from '../../hooks/useToast';
import { paymentService } from '../../services/payments';
import {
  Payment,
  PaymentStatus,
  PaymentSettings,
  PaymentListFilters,
  SubscriptionCategory,
  GeneratePaymentsRequest,
  GeneratePaymentsResult,
} from '../../types/payment';
import { MembershipType } from '../../types/member';
import { enumMatches, tryNormalizeEnumValue } from '../../utils/enumNormalize';
import { formatAthensDate } from '../../utils/athensTime';
import { formatMemberCardNumberForDisplay } from '../../utils/memberCardNumber';
import { Container, Stack, Grid } from '../../design-system/components/Layout';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading, Text } from '../../design-system/components/Typography';
import { Badge } from '../../design-system/components/Decorative';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import SearchBar from '../../components/common/SearchBar';
import { DataTable, Column } from '../../components/common/DataTable';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PaymentStatusModal from '../../components/admin/PaymentStatusModal';
import PaymentSettingsForm from '../../components/admin/PaymentSettingsForm';
import GeneratePaymentsForm from '../../components/admin/GeneratePaymentsForm';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { shadows } from '../../design-system/tokens/shadows';
import { animations } from '../../design-system/tokens/animations';
import {
  DocumentArrowDownIcon,
  TableCellsIcon,
  CurrencyEuroIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const AdminPaymentsContainer = styled(Container)`
  max-width: 100%;
  width: 100%;
`;

const MemberCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 160px;
  max-width: 260px;
`;

const MemberName = styled.span`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MemberMeta = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionsStack = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${spacing[2]};
`;

const FiltersSection = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]};
  align-items: end;
`;

const FilterLabel = styled.label`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
`;

const SelectStyled = styled.select`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  font-family: ${typography.fontFamily.primary};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.focus(props.theme.colors.primary)};
  }
`;

const DateInputStyled = styled.input`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  transition: ${animations.transition(
    ['border-color', 'box-shadow'],
    animations.duration.normal,
    animations.easing.easeOut
  )};
  font-family: ${typography.fontFamily.primary};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.focus(props.theme.colors.primary)};
  }
`;

const StatusBadge = styled(Badge)<{ $tone: 'pending' | 'paid' | 'overdue' | 'cancelled' | 'unknown' }>`
  background: ${props => {
    switch (props.$tone) {
      case 'paid':
        return props.theme.colors.success;
      case 'overdue':
        return props.theme.colors.error;
      case 'cancelled':
        return props.theme.colors.textMuted;
      case 'pending':
        return props.theme.colors.warning;
      default:
        return props.theme.colors.textSecondary;
    }
  }};
  color: white;
`;

function paymentStatusTone(
  status?: PaymentStatus | string | number | null
): 'pending' | 'paid' | 'overdue' | 'cancelled' | 'unknown' {
  const normalized = tryNormalizeEnumValue(PaymentStatus, status);
  switch (normalized) {
    case PaymentStatus.Pending:
      return 'pending';
    case PaymentStatus.Paid:
      return 'paid';
    case PaymentStatus.Overdue:
      return 'overdue';
    case PaymentStatus.Cancelled:
      return 'cancelled';
    default:
      return 'unknown';
  }
}

const StatsGrid = styled(Grid)`
  margin-bottom: ${spacing[8]};
`;

const StatCard = styled(Card)`
  padding: ${spacing[6]};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${spacing[2]};
`;

const StatLabel = styled(Text)`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
`;

const IconWrapperLarge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: ${spacing[2]};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const CardCentered = styled(Card)`
  padding: ${spacing[12]};
  text-align: center;
`;

const TextWithMarginTop = styled(Text)`
  margin-top: ${spacing[4]};
`;

function parseStatusParam(raw: string | null): PaymentStatus | '' {
  if (!raw) return '';
  const normalized = raw.trim().toLowerCase();
  if (normalized === 'pending' || raw === '1') return PaymentStatus.Pending;
  if (normalized === 'paid' || raw === '2') return PaymentStatus.Paid;
  if (normalized === 'overdue' || raw === '3') return PaymentStatus.Overdue;
  if (normalized === 'cancelled' || raw === '4') return PaymentStatus.Cancelled;
  const asNumber = Number(raw);
  if (
    asNumber === PaymentStatus.Pending ||
    asNumber === PaymentStatus.Paid ||
    asNumber === PaymentStatus.Overdue ||
    asNumber === PaymentStatus.Cancelled
  ) {
    return asNumber;
  }
  return '';
}

function parseYearParam(raw: string | null): number {
  const year = Number(raw);
  if (Number.isInteger(year) && year >= 2000 && year <= 2100) {
    return year;
  }
  return new Date().getFullYear();
}

const PAGE_SIZE = 25;

const AdminPayments: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { success: showSuccess, error: showError } = useToast();
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [yearFilter, setYearFilter] = useState<number>(() => parseYearParam(searchParams.get('year')));
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | ''>(() =>
    parseStatusParam(searchParams.get('status'))
  );
  const [membershipTypeFilter, setMembershipTypeFilter] = useState<string>('');
  const [paidFrom, setPaidFrom] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [page, setPage] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);

  const buildFilters = (includeLang = false): PaymentListFilters => {
    const filters: PaymentListFilters = { year: yearFilter };
    if (statusFilter) filters.status = statusFilter;
    if (membershipTypeFilter) {
      filters.membershipType = parseInt(membershipTypeFilter, 10);
    }
    if (debouncedSearch.trim()) filters.search = debouncedSearch.trim();
    if (paidFrom) filters.paidFrom = paidFrom;
    if (paidTo) filters.paidTo = paidTo;
    if (includeLang) {
      const lang = i18n.language?.toLowerCase().startsWith('el') || i18n.language?.toLowerCase().startsWith('gr')
        ? 'el'
        : 'en';
      filters.lang = lang;
    }
    return filters;
  };

  useEffect(() => {
    setYearFilter(parseYearParam(searchParams.get('year')));
    setStatusFilter(parseStatusParam(searchParams.get('status')));
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadPayments();
    loadPaymentSettings();
  }, [yearFilter, statusFilter, debouncedSearch, membershipTypeFilter, paidFrom, paidTo]);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const data = await paymentService.getAllPayments(buildFilters());
      setPayments(data);
    } catch (error) {
      console.error('Error loading payments:', error);
      showError(t('admin.payments.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      const settings = await paymentService.getPaymentSettings(yearFilter);
      setPaymentSettings(settings);
    } catch (error) {
      console.log('Payment settings not found for year', yearFilter);
      setPaymentSettings(null);
    }
  };

  const total = payments.length;
  const paginatedData = payments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const isPending = (p: Payment) => enumMatches(PaymentStatus, p.status, PaymentStatus.Pending);
  const isPaid = (p: Payment) => enumMatches(PaymentStatus, p.status, PaymentStatus.Paid);

  const stats = {
    total: payments.length,
    pending: payments.filter(isPending).length,
    paid: payments.filter(isPaid).length,
    totalRevenue: payments.filter(isPaid).reduce((sum, p) => sum + p.amount, 0),
  };

  const getStatusLabelLocal = (status?: PaymentStatus | string | number | null): string => {
    const normalized = tryNormalizeEnumValue(PaymentStatus, status);
    switch (normalized) {
      case PaymentStatus.Pending:
        return t('payments.status.pending');
      case PaymentStatus.Paid:
        return t('payments.status.paid');
      case PaymentStatus.Overdue:
        return t('payments.status.overdue');
      case PaymentStatus.Cancelled:
        return t('payments.status.cancelled');
      default:
        return '—';
    }
  };

  const getMembershipTypeLabel = (type?: MembershipType | string | number | null): string => {
    const normalized = tryNormalizeEnumValue(MembershipType, type);
    switch (normalized) {
      case MembershipType.Regular:
        return t('membership.regular');
      case MembershipType.Student:
        return t('membership.student');
      case MembershipType.Retired:
        return t('membership.retired');
      case MembershipType.Honorary:
        return t('membership.honorary');
      default:
        return '—';
    }
  };

  const subscriptionCategoryKeys: Record<SubscriptionCategory, string> = {
    [SubscriptionCategory.Regular]: 'payments.subscriptionCategoryRegular',
    [SubscriptionCategory.Student]: 'payments.subscriptionCategoryStudent',
    [SubscriptionCategory.Retired]: 'payments.subscriptionCategoryRetired',
    [SubscriptionCategory.Honorary]: 'payments.subscriptionCategoryHonorary',
    [SubscriptionCategory.InstitutionCategory0]: 'payments.subscriptionCategoryInstitution0',
    [SubscriptionCategory.InstitutionCategory1]: 'payments.subscriptionCategoryInstitution1',
    [SubscriptionCategory.InstitutionCategory2]: 'payments.subscriptionCategoryInstitution2',
    [SubscriptionCategory.InstitutionCategory3]: 'payments.subscriptionCategoryInstitution3',
    [SubscriptionCategory.InstitutionCategory4]: 'payments.subscriptionCategoryInstitution4',
    [SubscriptionCategory.InstitutionCategory5]: 'payments.subscriptionCategoryInstitution5',
    [SubscriptionCategory.InstitutionBenefactor]: 'payments.subscriptionCategoryInstitutionBenefactor',
  };
  const getSubscriptionCategoryLabel = (
    cat?: SubscriptionCategory | string | number | null
  ): string => {
    const normalized = tryNormalizeEnumValue(SubscriptionCategory, cat);
    return normalized != null ? t(subscriptionCategoryKeys[normalized]) : '—';
  };

  const handleMarkAsPaid = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async (request: Record<string, unknown>) => {
    if (!selectedPayment) return;

    try {
      await paymentService.updatePaymentStatus(selectedPayment.id, {
        ...request,
        status: PaymentStatus.Paid,
      });
      showSuccess(t('admin.payments.paymentMarkedAsPaid'));
      setShowStatusModal(false);
      setSelectedPayment(null);
      await loadPayments();
    } catch (error) {
      showError(t('admin.payments.updateError'));
    }
  };

  const handleDownloadReceipt = async (paymentId: string) => {
    try {
      const blob = await paymentService.generateReceipt(paymentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt_${payments.find(p => p.id === paymentId)?.year}_${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess(t('admin.payments.receiptGenerated'));
    } catch (error) {
      console.error('Error generating receipt:', error);
      showError(t('admin.payments.receiptError'));
    }
  };

  const handleSettingsSave = async (settings: PaymentSettings) => {
    try {
      await paymentService.updatePaymentSettings(settings);
      showSuccess(t('admin.payments.settingsSaved'));
      setShowSettingsModal(false);
      await loadPaymentSettings();
    } catch (error) {
      showError(t('admin.payments.settingsError'));
    }
  };

  const handleGeneratePayments = async (
    request: GeneratePaymentsRequest
  ): Promise<GeneratePaymentsResult> => {
    const result = await paymentService.generateYearlyPayments(request);
    showSuccess(
      t('admin.payments.generateSuccessToast', {
        created: result.individualsCreated + result.institutionsCreated,
        year: result.year,
      })
    );
    await loadPaymentSettings();
    await loadPayments();
    return result;
  };

  const handleExportExcel = async () => {
    if (payments.length === 0) {
      showError(t('admin.payments.exportEmpty'));
      return;
    }
    setExporting(true);
    try {
      const { blob, fileName } = await paymentService.exportPayments(buildFilters(true));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess(t('admin.payments.exportSuccess'));
    } catch (error) {
      console.error('Error exporting payments:', error);
      showError(t('admin.payments.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return `€${amount.toFixed(2)}`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    return formatAthensDate(dateString, i18n.language);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const paymentColumns: Column<Payment>[] = [
    {
      key: 'member',
      header: t('payments.member'),
      wrap: true,
      render: (payment) => (
        <MemberCell
          title={[
            payment.memberName,
            payment.memberEmail,
            formatMemberCardNumberForDisplay(payment.memberCardNumber) || payment.memberCardNumber,
          ]
            .filter(Boolean)
            .join('\n')}
        >
          <MemberName>{payment.memberName || '—'}</MemberName>
          {payment.memberEmail && <MemberMeta>{payment.memberEmail}</MemberMeta>}
          {payment.memberCardNumber && (
            <MemberMeta>
              {t('register.memberCardNumber')}:{' '}
              {formatMemberCardNumberForDisplay(payment.memberCardNumber)}
            </MemberMeta>
          )}
        </MemberCell>
      ),
    },
    {
      key: 'year',
      header: t('payments.year'),
      render: (payment) => payment.year,
    },
    {
      key: 'amount',
      header: t('payments.amount'),
      render: (payment) => formatCurrency(payment.amount),
    },
    {
      key: 'subscriptionCategory',
      header: t('payments.subscriptionCategory'),
      render: (payment) => getSubscriptionCategoryLabel(payment.subscriptionCategory),
    },
    {
      key: 'membershipType',
      header: t('payments.membershipType'),
      render: (payment) => getMembershipTypeLabel(payment.membershipType),
    },
    {
      key: 'status',
      header: t('payments.statusLabel') || t('payments.status'),
      render: (payment) => (
        <StatusBadge $tone={paymentStatusTone(payment.status)} size="sm" shape="pill">
          {getStatusLabelLocal(payment.status)}
        </StatusBadge>
      ),
    },
    {
      key: 'paidAt',
      header: t('payments.paidAt'),
      render: (payment) => formatDate(payment.paidAt),
    },
    {
      key: 'actions',
      header: t('admin.actions'),
      actions: (payment) => (
        <ActionsStack>
          {enumMatches(PaymentStatus, payment.status, PaymentStatus.Pending) && (
            <Button
              variant="gradient"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAsPaid(payment);
              }}
            >
              {t('admin.payments.markAsPaid')}
            </Button>
          )}
          {enumMatches(PaymentStatus, payment.status, PaymentStatus.Paid) && (
            <Button
              variant="outline"
              size="sm"
              title={
                payment.hasReceipt
                  ? t('payments.downloadReceipt')
                  : t('admin.payments.generateReceipt')
              }
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadReceipt(payment.id);
              }}
            >
              <DocumentArrowDownIcon style={{ width: 16, height: 16, marginRight: spacing[2] }} />
              {payment.hasReceipt
                ? t('payments.downloadReceipt')
                : t('admin.payments.generateReceipt')}
            </Button>
          )}
        </ActionsStack>
      ),
    },
  ];

  return (
    <AdminPaymentsContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('admin.payments.title')}
          subtitle={
            !loading && total > 0
              ? t('admin.showingResults', {
                  start: (page - 1) * PAGE_SIZE + 1,
                  end: Math.min(page * PAGE_SIZE, total),
                  total,
                }) || `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} of ${total}`
              : undefined
          }
          pattern={true}
          actions={
            <>
              <Button
                variant="outline"
                onClick={handleExportExcel}
                disabled={exporting || loading}
                ripple
              >
                <IconWrapperLarge>
                  <TableCellsIcon />
                </IconWrapperLarge>
                {exporting ? t('admin.payments.exporting') : t('admin.payments.exportExcel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowGenerateModal(true)}
                ripple
              >
                <IconWrapperLarge>
                  <SparklesIcon />
                </IconWrapperLarge>
                {t('admin.payments.generateAction')}
              </Button>
              <Button
                variant="gradient"
                onClick={async () => {
                  try {
                    const settings = await paymentService.getPaymentSettings(yearFilter);
                    setPaymentSettings(settings);
                    setShowSettingsModal(true);
                  } catch {
                    showError(t('admin.payments.settingsLoadError', {
                      defaultValue: 'Could not load payment settings for this year.',
                    }));
                  }
                }}
                glow
                ripple
              >
                <IconWrapperLarge>
                  <CurrencyEuroIcon />
                </IconWrapperLarge>
                {t('admin.payments.settings')}
              </Button>
            </>
          }
        />

        <StatsGrid columns={{ sm: 2, md: 4 }} gap="md">
          <StatCard variant="elevated" accentBorder="top">
            <StatValue>{stats.total}</StatValue>
            <StatLabel>{t('admin.payments.totalPayments')}</StatLabel>
          </StatCard>
          <StatCard variant="elevated" accentBorder="top">
            <StatValue>{stats.pending}</StatValue>
            <StatLabel>{t('admin.payments.pendingPayments')}</StatLabel>
          </StatCard>
          <StatCard variant="elevated" accentBorder="top">
            <StatValue>{stats.paid}</StatValue>
            <StatLabel>{t('admin.payments.paidPayments')}</StatLabel>
          </StatCard>
          <StatCard variant="elevated" accentBorder="top">
            <StatValue>{formatCurrency(stats.totalRevenue)}</StatValue>
            <StatLabel>{t('admin.payments.totalRevenue')}</StatLabel>
          </StatCard>
        </StatsGrid>

        <FiltersSection variant="elevated" accentBorder="top">
          <Stack direction="column" spacing="md">
            <Heading variant="h3" color="text">
              {t('admin.filters')}
            </Heading>
            <FiltersGrid>
              <Stack direction="column" spacing="sm">
                <FilterLabel>{t('admin.payments.search')}</FilterLabel>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  onSearch={setDebouncedSearch}
                  placeholder={t('admin.payments.searchPlaceholder')}
                  loading={loading}
                  fullWidth
                />
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-payments-year">{t('payments.year')}</FilterLabel>
                <SelectStyled
                  id="admin-payments-year"
                  value={yearFilter}
                  onChange={(e) => {
                    setYearFilter(parseInt(e.target.value, 10));
                    setPage(1);
                  }}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </SelectStyled>
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-payments-status">{t('payments.statusLabel') || t('payments.status')}</FilterLabel>
                <SelectStyled
                  id="admin-payments-status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value ? (parseInt(e.target.value, 10) as PaymentStatus) : '');
                    setPage(1);
                  }}
                >
                  <option value="">{t('admin.all')}</option>
                  <option value={PaymentStatus.Pending.toString()}>
                    {t('payments.status.pending')}
                  </option>
                  <option value={PaymentStatus.Paid.toString()}>
                    {t('payments.status.paid')}
                  </option>
                  <option value={PaymentStatus.Overdue.toString()}>
                    {t('payments.status.overdue')}
                  </option>
                  <option value={PaymentStatus.Cancelled.toString()}>
                    {t('payments.status.cancelled')}
                  </option>
                </SelectStyled>
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-payments-membership-type">{t('admin.membershipType')}</FilterLabel>
                <SelectStyled
                  id="admin-payments-membership-type"
                  value={membershipTypeFilter}
                  onChange={(e) => {
                    setMembershipTypeFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t('admin.all')}</option>
                  <option value={MembershipType.Regular.toString()}>
                    {t('membership.regular')}
                  </option>
                  <option value={MembershipType.Student.toString()}>
                    {t('membership.student')}
                  </option>
                  <option value={MembershipType.Retired.toString()}>
                    {t('membership.retired')}
                  </option>
                  <option value={MembershipType.Honorary.toString()}>
                    {t('membership.honorary')}
                  </option>
                </SelectStyled>
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-payments-paid-from">{t('admin.payments.paidFrom')}</FilterLabel>
                <DateInputStyled
                  id="admin-payments-paid-from"
                  type="date"
                  value={paidFrom}
                  onChange={(e) => {
                    setPaidFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-payments-paid-to">{t('admin.payments.paidTo')}</FilterLabel>
                <DateInputStyled
                  id="admin-payments-paid-to"
                  type="date"
                  value={paidTo}
                  min={paidFrom || undefined}
                  onChange={(e) => {
                    setPaidTo(e.target.value);
                    setPage(1);
                  }}
                />
              </Stack>
            </FiltersGrid>
          </Stack>
        </FiltersSection>

        {loading ? (
          <CardCentered variant="filled">
            <LoadingSpinner size="lg" />
            <TextWithMarginTop size="lg" color="secondary">
              {t('common.loading')}
            </TextWithMarginTop>
          </CardCentered>
        ) : (
          <DataTable<Payment>
            data={paginatedData}
            columns={paymentColumns}
            loading={false}
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            emptyMessage={t('admin.payments.noPayments')}
          />
        )}

        {showStatusModal && selectedPayment && (
          <PaymentStatusModal
            payment={selectedPayment}
            onClose={() => {
              setShowStatusModal(false);
              setSelectedPayment(null);
            }}
            onSave={handleStatusUpdate}
          />
        )}

        {showSettingsModal && (
          <PaymentSettingsForm
            year={yearFilter}
            settings={paymentSettings}
            onClose={() => setShowSettingsModal(false)}
            onSave={handleSettingsSave}
          />
        )}

        {showGenerateModal && (
          <GeneratePaymentsForm
            year={yearFilter}
            settings={paymentSettings}
            onClose={() => setShowGenerateModal(false)}
            onGenerate={handleGeneratePayments}
          />
        )}
      </Stack>
    </AdminPaymentsContainer>
  );
};

export default AdminPayments;
